import { useState, useRef, useEffect } from "react";
import { User, LogOut, Edit2, Save, X, Eye, EyeOff, ChevronDown, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const API = window.location.origin;

export default function ProfileDropdown({ user, onUserUpdate, onLogout }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const dropRef = useRef(null);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    mobile: user?.mobile || "",
    new_password: "",
  });

  // Sync form when user changes
  useEffect(() => {
    setForm({ name: user?.name || "", email: user?.email || "", mobile: user?.mobile || "", new_password: "" });
  }, [user]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const initials = (user?.name || "?").split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);

  const getPlanColor = () => {
    if (user?.plan === "unlimited") return "from-yellow-400 to-orange-500";
    if (["premium", "lokesh_notes", "tier2_pro", "standard", "basic"].includes(user?.plan)) return "from-green-400 to-emerald-600";
    if (user?.plan && user?.plan !== "free") return "from-teal-400 to-green-500";
    return "from-gray-400 to-gray-600";
  };

  const getPlanLabel = () => {
    if (user?.plan === "unlimited") return "Unlimited";
    if (["premium", "lokesh_notes", "tier2_pro", "standard", "basic"].includes(user?.plan)) return "Premium";
    if (user?.plan && user?.plan !== "free") return "Paid";
    return "Free";
  };

  const handleSave = async () => {
    if (!user?.id || !user?.session_token) {
      toast.error("Session expired. Please login again.");
      return;
    }
    setSaving(true);
    try {
      const payload = { user_id: user.id, session_token: user.session_token };
      if (form.name.trim() && form.name.trim() !== user.name) payload.name = form.name.trim();
      if (form.email.trim() && form.email.trim() !== user.email) payload.email = form.email.trim();
      if (form.mobile.trim() && form.mobile.trim() !== user.mobile) payload.mobile = form.mobile.trim();
      if (form.new_password.trim()) payload.new_password = form.new_password.trim();

      if (Object.keys(payload).length <= 2) {
        toast("No changes to save");
        setEditing(false);
        return;
      }

      const { data } = await axios.put(`${API}/api/auth/update-profile`, payload);
      if (data.success) {
        // Merge new user data with session info
        const updated = { ...user, ...data.user };
        onUserUpdate(updated);
        setForm({ ...form, new_password: "" });
        setEditing(false);
        toast.success("Profile updated!");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    setForm({ name: user?.name || "", email: user?.email || "", mobile: user?.mobile || "", new_password: "" });
    setEditing(false);
  };

  return (
    <div className="relative" ref={dropRef}>
      {/* Trigger */}
      <button
        onClick={() => { setOpen(o => !o); setEditing(false); }}
        data-testid="profile-btn"
        className="flex items-center gap-2 px-2 py-1.5 rounded-xl hover:bg-gray-100 transition-colors group"
      >
        <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${getPlanColor()} flex items-center justify-center text-white text-xs font-bold shadow-sm flex-shrink-0`}>
          {initials}
        </div>
        <span className="text-sm font-medium text-gray-700 hidden sm:block max-w-[100px] truncate">{user?.name?.split(" ")[0]}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${open ? "rotate-180" : ""} hidden sm:block`} />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden z-50"
          data-testid="profile-dropdown">
          {/* Header */}
          <div className={`bg-gradient-to-r ${getPlanColor()} p-4`}>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-white text-lg font-bold">
                {initials}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-bold truncate">{user?.name}</p>
                <p className="text-white/75 text-xs truncate">{user?.mobile}</p>
                <span className="inline-block mt-0.5 bg-white/25 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">
                  {getPlanLabel()}
                </span>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="p-4">
            {!editing ? (
              /* View mode */
              <div className="space-y-2.5">
                <InfoRow label="Name" value={user?.name} />
                <InfoRow label="Mobile" value={user?.mobile} />
                <InfoRow label="Email" value={user?.email || "Not set"} muted={!user?.email} />
                <InfoRow label="Password" value="••••••••" />

                <button
                  onClick={() => setEditing(true)}
                  data-testid="edit-profile-btn"
                  className="w-full mt-3 flex items-center justify-center gap-2 bg-gray-900 text-white text-sm font-semibold py-2.5 rounded-xl hover:bg-gray-800 transition-colors"
                >
                  <Edit2 className="w-3.5 h-3.5" />
                  Edit Profile
                </button>

                {/* Change Subject button - only show if user has selected a category and it's not PRT */}
                {user?.selected_category && user.selected_category !== 'prt' && (
                  <button
                    onClick={() => {
                      setOpen(false);
                      // Clear selected category and redirect to category page
                      const formData = new FormData();
                      formData.append('user_id', user.id);
                      formData.append('session_token', user.session_token || localStorage.getItem('session_token'));
                      formData.append('selected_category', 'prt'); // Reset to PRT (unlocked)
                      
                      fetch(`${API}/api/auth/update-selected-category`, {
                        method: 'POST',
                        body: formData
                      }).then(() => {
                        const updatedUser = { ...user, selected_category: null };
                        onUserUpdate(updatedUser);
                        // Clear subject selection so App.js redirects properly
                        localStorage.removeItem('user_subject_selection');
                        localStorage.removeItem('subjectSelection');
                        localStorage.removeItem('notes_state');
                        // Full page reload to reset App.js subjectSelection state
                        window.location.href = '/category';
                      }).catch(err => {
                        console.error("Failed to reset category:", err);
                        window.location.href = '/category';
                      });
                    }}
                    data-testid="change-subject-btn"
                    className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white text-sm font-semibold py-2.5 rounded-xl hover:bg-blue-700 transition-colors"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    Change Subject
                  </button>
                )}

                <button
                  onClick={() => { setOpen(false); onLogout(); }}
                  data-testid="profile-logout-btn"
                  className="w-full flex items-center justify-center gap-2 text-red-600 text-sm font-medium py-2 rounded-xl hover:bg-red-50 transition-colors"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  Logout
                </button>
              </div>
            ) : (
              /* Edit mode */
              <div className="space-y-3">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Edit Profile</p>

                <FormField label="Name" value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} placeholder="Your name" testid="profile-name-input" />
                <FormField label="Email" value={form.email} onChange={v => setForm(f => ({ ...f, email: v }))} placeholder="your@email.com" type="email" testid="profile-email-input" />
                <FormField label="Mobile" value={form.mobile} onChange={v => setForm(f => ({ ...f, mobile: v }))} placeholder="10 digit number" type="tel" testid="profile-mobile-input" />

                {/* Password */}
                <div>
                  <label className="text-xs font-medium text-gray-500 block mb-1">New Password <span className="text-gray-400">(leave blank to keep)</span></label>
                  <div className="relative">
                    <input
                      type={showPass ? "text" : "password"}
                      value={form.new_password}
                      onChange={e => setForm(f => ({ ...f, new_password: e.target.value }))}
                      placeholder="New password"
                      data-testid="profile-password-input"
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm pr-10 focus:outline-none focus:border-gray-900"
                    />
                    <button type="button" onClick={() => setShowPass(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                      {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex gap-2 pt-1">
                  <button
                    onClick={cancelEdit}
                    className="flex-1 flex items-center justify-center gap-1.5 border border-gray-200 text-gray-600 text-sm py-2.5 rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    <X className="w-3.5 h-3.5" /> Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    data-testid="save-profile-btn"
                    className="flex-1 flex items-center justify-center gap-1.5 bg-gray-900 text-white text-sm font-semibold py-2.5 rounded-xl hover:bg-gray-800 transition-colors disabled:opacity-60"
                  >
                    {saving ? (
                      <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    ) : (
                      <><Save className="w-3.5 h-3.5" /> Save</>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function InfoRow({ label, value, muted }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
      <span className="text-xs text-gray-400 font-medium w-16">{label}</span>
      <span className={`text-sm font-medium truncate max-w-[180px] ${muted ? "text-gray-300 italic" : "text-gray-800"}`}>{value}</span>
    </div>
  );
}

function FormField({ label, value, onChange, placeholder, type = "text", testid }) {
  return (
    <div>
      <label className="text-xs font-medium text-gray-500 block mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-gray-900"
      />
    </div>
  );
}
